import AppPageLayout from '@components/app/AppPageLayout';
import getAppProps from '@components/app/getAppProps';
import { AuthenticatedPageComponentProps } from '@components/app/types';
import InfoPage from '@components/landing/InfoPage';
import { graphql } from '@graphql/gql';
import { Event_Info_PageQuery as EventInfoPageQuery } from '@graphql/gql/graphql';
import { eventErrorPageHandling } from '@server/eventErrorPageHandling';
import { getEventInfoProps, isEventInfoError } from '@server/getEventInfoProps';
import parseEnvironment from '@server/parseEnvironment';
import { GetServerSideProps } from 'next';
import React from 'react';
import { v4 as uuid } from 'uuid';

type InfoPageProps = AuthenticatedPageComponentProps & {
  eventInfo: EventInfoPageQuery['eventInfo'];
  eventId: string;
};

/**
 * Query to use on the server to fetch for all the information needed by this page
 */
const eventInfoQuery = graphql(`
  query EVENT_INFO_PAGE($eventId: ID!, $features: [String!]) {
    eventInfo(eventId: $eventId, features: $features) {
      id
      title
      description
      start
      end
      defaultLocale
      sessions {
        id
        name
        description
        end
        start
      }
      features {
        id
        isEnabled
        type
        featureCode
      }
      speakers {
        id
        firstName
        lastName
        title
        company
        profileImageUri
        biography
      }
      isPublished
      isArchived
    }
  }
`);

export const getServerSideProps: GetServerSideProps = async ctx => {
  const httpLogRequestId = uuid();

  const appProps = await getAppProps(ctx);

  if ('redirect' in appProps) {
    if (ctx.query.environment) {
      // Redirect to public landing page with the environment query param
      return {
        ...appProps,
        redirect: {
          ...appProps.redirect,
          destination: `${appProps.redirect.destination}/environment/${ctx.query.environment}`
        }
      };
    }
    // Redirect to public landing page (login)
    return appProps;
  }

  const environment = parseEnvironment(ctx);

  // User is authorized. Get the event info
  const eventInfo = await getEventInfoProps<EventInfoPageQuery['eventInfo']>(
    ctx,
    eventInfoQuery,
    environment,
    httpLogRequestId
  );

  if (isEventInfoError(eventInfo)) {
    const {
      params: { eventId }
    } = ctx;

    const redirectErrorPage = eventErrorPageHandling(
      !eventInfo.error ? eventInfo : undefined,
      httpLogRequestId,
      eventId as string
    );

    if (redirectErrorPage) {
      return { redirect: redirectErrorPage };
    }

    throw new Error(eventInfo.error);
  }

  // In place for the chance we are missing the session data.
  // This could happen if there's an unforeseen error
  // that does not result in the query above throwing an error.
  if (!eventInfo.sessions || !eventInfo.sessions[0]) {
    throw new Error('No Session data');
  }

  return {
    props: {
      eventInfo,
      eventId: appProps.eventId,
      appProps
    }
  };
};

function Info(props: InfoPageProps): JSX.Element {
  return <InfoPage {...props} />;
}

Info.getLayout = AppPageLayout;

export default Info;
